import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  simulateEpoWorkflow,
  validateEpoWorkflowConfiguration,
} from "../../../services/admin";
import {
  GetAllEpoDropdown,
  GetCurrencyValue,
} from "../../../services/expense-purchase-order";

const get = (obj, camelKey, pascalKey, fallback = null) => {
  if (!obj) return fallback;
  if (Object.prototype.hasOwnProperty.call(obj, camelKey)) return obj[camelKey];
  if (Object.prototype.hasOwnProperty.call(obj, pascalKey))
    return obj[pascalKey];
  return fallback;
};

const list = (value) => (Array.isArray(value) ? value : []);
const normalizeText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(numeric);
};

const getErrorMessage = (
  error,
  fallback = "Unable to simulate EPO workflow.",
) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.Message) return data.Message;
  if (error?.message) return error.message;
  return fallback;
};

const employeeIsActive = (employee) => {
  const value = employee?.isActive;
  if (value === false || value === 0 || value === "0") return false;
  if (typeof value === "string") {
    return !["N", "NO", "FALSE", "INACTIVE"].includes(value.toUpperCase());
  }
  return true;
};

const dedupeEmployees = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    const key =
      row?.userId != null
        ? `USER:${row.userId}`
        : row?.personId != null
        ? `PERSON:${row.personId}`
        : `NAME:${normalizeText(
            row?.displayName || row?.fullName,
          ).toLowerCase()}`;
    if (!map.has(key)) map.set(key, row);
  });
  return [...map.values()];
};

const filterEmployees = (options, state) => {
  const terms = normalizeText(state?.inputValue)
    .toLowerCase()
    .split(" ")
    .filter(Boolean);
  if (!terms.length) return options;

  return options.filter((employee) => {
    const searchable = normalizeText(
      `${employee?.displayName || ""} ${employee?.fullName || ""} ${
        employee?.employeeCode || ""
      } ${employee?.personId || ""} ${employee?.userId || ""} ${
        employee?.departmentName || ""
      }`,
    ).toLowerCase();
    return terms.every((term) => searchable.includes(term));
  });
};

const normalizeCurrency = (row) => ({
  id: Number(get(row, "id", "Id", 0)),
  name: normalizeText(
    get(
      row,
      "currency",
      "Currency",
      get(row, "name", "Name", get(row, "currencyName", "CurrencyName", "")),
    ),
  ),
  code: normalizeText(
    get(row, "currencyCode", "CurrencyCode", get(row, "code", "Code", "")),
  ).toUpperCase(),
});

const extractRate = (data) => {
  const candidates = [
    data,
    data?.result,
    data?.Result,
    data?.currencyValue,
    data?.CurrencyValue,
    data?.value,
    data?.Value,
    data?.rate,
    data?.Rate,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
};

const SummaryCard = ({ label, value, caption, icon }) => (
  <Paper
    variant="outlined"
    sx={{
      height: "100%",
      px: 1.15,
      py: 0.8,
      borderRadius: 1.5,
      bgcolor: "background.paper",
    }}
  >
    <Stack direction="row" spacing={0.9} alignItems="center">
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: "action.hover",
          color: "primary.main",
          "& svg": { fontSize: 16 },
        }}
      >
        {icon}
      </Avatar>
      <Box minWidth={0}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1, fontSize: "0.68rem" }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          noWrap
          sx={{ lineHeight: 1.15, mt: 0.15 }}
        >
          {value}
        </Typography>
        {caption ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            noWrap
            sx={{ mt: 0.1, fontSize: "0.66rem" }}
          >
            {caption}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  </Paper>
);

const PersonRow = ({ person }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Avatar sx={{ width: 34, height: 34 }}>
      {normalizeText(get(person, "fullName", "FullName", "?"))
        .charAt(0)
        .toUpperCase() || "?"}
    </Avatar>
    <Box minWidth={0}>
      <Typography variant="body2" fontWeight={700}>
        {get(person, "fullName", "FullName", "Unknown approver")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        UserId {get(person, "userId", "UserId", "—")}
        {get(person, "departmentName", "DepartmentName", "")
          ? ` • ${get(person, "departmentName", "DepartmentName", "")}`
          : ""}
      </Typography>
      {get(person, "emailAddress", "EmailAddress", "") ? (
        <Typography variant="caption" color="text.secondary">
          {get(person, "emailAddress", "EmailAddress", "")}
        </Typography>
      ) : null}
    </Box>
  </Stack>
);

const boundaryText = (source, key, amount, inclusive, isMin) => {
  const normalizedSource = normalizeText(source).toUpperCase();
  if (!normalizedSource || normalizedSource === "NONE") return "No boundary";
  const symbol = isMin ? (inclusive ? "≥" : ">") : inclusive ? "≤" : "<";
  const label = key ? `${normalizedSource} / ${key}` : normalizedSource;
  return `${symbol} ${formatCurrency(amount)} • ${label}`;
};

const EmailRecipients = ({ label, value }) => {
  const recipients = String(value || "")
    .split(/[;,]+/)
    .map((email) => email.trim())
    .filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 32,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          width: 35,
          flexShrink: 0,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={recipients.join(", ")}
      >
        {recipients.length ? recipients.join(", ") : "—"}
      </Typography>
    </Box>
  );
};

const MailPreview = ({ mail }) => {
  const to = get(mail, "to", "To", "");
  const cc = get(mail, "cc", "Cc", "");
  const bcc = get(mail, "bcc", "Bcc", "");
  const subject = get(mail, "subject", "Subject", "");
  const bodyHtml = get(mail, "bodyHtml", "BodyHtml", "");
  const eventCode = get(mail, "eventCode", "EventCode", "MAIL");
  const ruleCode = get(mail, "workflowRuleCode", "WorkflowRuleCode", "");
  const stepCode = get(mail, "workflowStepCode", "WorkflowStepCode", "");

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {eventCode}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {ruleCode || "All Rules"}
              {stepCode ? ` • ${stepCode}` : ""}
            </Typography>
          </Box>

          <Chip
            size="small"
            color="info"
            variant="outlined"
            label="SIMULATION — NOT SENT"
          />
        </Stack>
      </Box>

      <Box sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              py: 0.75,
            }}
          >
            <Typography variant="body2">
              <strong>TO:</strong>&nbsp;&nbsp;{to || "—"}
            </Typography>

            <Typography color="text.secondary">|</Typography>

            <Typography variant="body2">
              <strong>CC:</strong>&nbsp;&nbsp;{cc || "—"}
            </Typography>

            <Typography color="text.secondary">|</Typography>

            <Typography variant="body2">
              <strong>BCC:</strong>&nbsp;&nbsp;{bcc || "—"}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary">
              SUBJECT
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {subject || "—"}
            </Typography>
          </Box>

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "#fff",
              p: 1,
              overflow: "auto",
              maxHeight: 550,
            }}
          >
            <iframe
              title={`Mail Preview - ${eventCode}`}
              srcDoc={bodyHtml || "<div>No email body.</div>"}
              style={{
                width: "100%",
                minHeight: "450px",
                border: "0",
                background: "#fff",
              }}
              sandbox=""
            />
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

const EpoWorkflowSimulator = ({
  employeeRows = [],
  employeeLoadError = "",
}) => {
  const employees = useMemo(
    () =>
      dedupeEmployees(
        list(employeeRows).filter(
          (employee) =>
            employee &&
            employee.userId != null &&
            normalizeText(employee.displayName || employee.fullName) &&
            employeeIsActive(employee),
        ),
      ).sort((a, b) =>
        normalizeText(a.displayName || a.fullName).localeCompare(
          normalizeText(b.displayName || b.fullName),
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [employeeRows],
  );

  const [creator, setCreator] = useState(null);
  const [amount, setAmount] = useState(0);
  const [isItPurchase, setIsItPurchase] = useState(false);
  const [currencies, setCurrencies] = useState([
    { id: 1, name: "Indian Rupee", code: "INR" },
  ]);
  const [selectedCurrency, setSelectedCurrency] = useState({
    id: 1,
    name: "Indian Rupee",
    code: "INR",
  });
  const [exchangeRate, setExchangeRate] = useState(1);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [currencyError, setCurrencyError] = useState("");
  const [validation, setValidation] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  const loadValidation = async () => {
    try {
      setValidationLoading(true);
      const response = await validateEpoWorkflowConfiguration();
      setValidation(response?.data || null);
    } catch (validationError) {
      setValidation({
        isValid: false,
        errorCount: 1,
        warningCount: 0,
        issues: [
          {
            severity: "ERROR",
            issueCode: "VALIDATION_API_ERROR",
            message: getErrorMessage(
              validationError,
              "Unable to validate workflow configuration.",
            ),
          },
        ],
      });
    } finally {
      setValidationLoading(false);
    }
  };

  useEffect(() => {
    loadValidation();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCurrencies = async () => {
      try {
        setCurrencyError("");
        const response = await GetAllEpoDropdown();
        const data = response?.data || {};
        const rows =
          data?.currencyDropDown ||
          data?.CurrencyDropDown ||
          data?.currencyDropdown ||
          data?.CurrencyDropdown ||
          [];

        const normalized = list(rows)
          .map(normalizeCurrency)
          .filter((row) => row.id > 0 && row.code);

        const map = new Map();
        normalized.forEach((row) => map.set(`${row.id}-${row.code}`, row));
        const values = [...map.values()];

        if (!values.some((row) => row.id === 1)) {
          values.unshift({ id: 1, name: "Indian Rupee", code: "INR" });
        }

        if (!cancelled) {
          setCurrencies(values);
          const inr = values.find((row) => row.id === 1) || values[0];
          setSelectedCurrency(inr);
        }
      } catch (currencyLoadError) {
        if (!cancelled) {
          setCurrencyError(
            getErrorMessage(
              currencyLoadError,
              "Unable to load currencies. INR remains available.",
            ),
          );
        }
      }
    };

    loadCurrencies();
    return () => {
      cancelled = true;
    };
  }, []);

  const changeCurrency = async (currency) => {
    const next = currency || { id: 1, name: "Indian Rupee", code: "INR" };
    setSelectedCurrency(next);
    setResult(null);
    setCurrencyError("");

    if (Number(next.id) === 1 || next.code === "INR") {
      setExchangeRate(1);
      return;
    }

    try {
      setCurrencyLoading(true);
      const response = await GetCurrencyValue(next.code);
      const rate = extractRate(response?.data);
      if (!rate) {
        throw new Error(
          `No numeric INR exchange rate was returned for ${next.code}.`,
        );
      }
      setExchangeRate(rate);
    } catch (rateError) {
      setExchangeRate("");
      setCurrencyError(
        getErrorMessage(rateError, "Unable to load exchange rate."),
      );
    } finally {
      setCurrencyLoading(false);
    }
  };

  const runSimulation = async () => {
    setError("");
    setResult(null);

    if (!creator?.userId) {
      setError("Select an EPO creator.");
      return;
    }

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < 0) {
      setError("Enter a valid EPO amount.");
      return;
    }

    const isInr =
      Number(selectedCurrency?.id || 1) === 1 ||
      selectedCurrency?.code === "INR";
    const numericRate = isInr ? 1 : Number(exchangeRate);
    if (!isInr && (!Number.isFinite(numericRate) || numericRate <= 0)) {
      setError("Enter a valid INR exchange rate for the selected currency.");
      return;
    }

    try {
      setRunning(true);
      const response = await simulateEpoWorkflow({
        creatorUserId: Number(creator.userId),
        amount: numericAmount,
        isItPurchase,
        currencyType: Number(selectedCurrency?.id || 1),
        dollarPrice: isInr ? null : numericRate,
      });
      setResult(response?.data || null);
      await loadValidation();
    } catch (simulationError) {
      setError(getErrorMessage(simulationError));
    } finally {
      setRunning(false);
    }
  };

  const summary = get(result, "summary", "Summary", null);
  const evaluations = list(
    get(result, "ruleEvaluations", "RuleEvaluations", []),
  );
  const steps = list(get(result, "steps", "Steps", []));
  const approvers = list(get(result, "approvers", "Approvers", []));
  const issues = list(get(result, "issues", "Issues", []));
  const mails = list(get(result, "mails", "Mails", []));
  const validationIssues = list(get(validation, "issues", "Issues", []));
  const validationIsValid = Boolean(
    get(validation, "isValid", "IsValid", false),
  );
  const validationReady = validation !== null && !validationLoading;
  const validationBlocked = validationReady && !validationIsValid;

  const approversForStep = (step) => {
    const stepId = get(step, "workflowStepId", "WorkflowStepId", null);
    return approvers.filter(
      (row) =>
        Number(get(row, "workflowStepId", "WorkflowStepId", 0)) ===
        Number(stepId),
    );
  };

  return (
    <Stack spacing={1.5}>
      <Alert severity="info">
        This simulator is read-only. Routing is calculated by{" "}
        <strong>master.ResolveEpoWorkflow</strong> from the current DB
        configuration. It does not create or update any real EPO record.
      </Alert>

      {employeeLoadError ? (
        <Alert severity="warning">{employeeLoadError}</Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.4 }}>
        <Stack spacing={1}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Typography variant="h6">Configuration Health</Typography>
              <Typography variant="body2" color="text.secondary">
                Validation checks the complete rule, step and authority
                configuration before simulation.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {validationLoading ? <CircularProgress size={20} /> : null}
              <Chip
                color={validationIsValid ? "success" : "error"}
                variant="outlined"
                label={validationIsValid ? "VALID" : "CHECK CONFIGURATION"}
              />
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadValidation}
                disabled={validationLoading}
              >
                Recheck
              </Button>
            </Stack>
          </Stack>

          {validation ? (
            <Grid container spacing={0.8}>
              <Grid item xs={6} md={2.4}>
                <SummaryCard
                  label="Rules"
                  value={get(validation, "ruleCount", "RuleCount", 0)}
                  icon={<RuleOutlinedIcon />}
                />
              </Grid>
              <Grid item xs={6} md={2.4}>
                <SummaryCard
                  label="Steps"
                  value={get(validation, "stepCount", "StepCount", 0)}
                  icon={<HowToRegOutlinedIcon />}
                />
              </Grid>
              <Grid item xs={6} md={2.4}>
                <SummaryCard
                  label="Authorities"
                  value={get(validation, "authorityCount", "AuthorityCount", 0)}
                  icon={<HowToRegOutlinedIcon />}
                />
              </Grid>
              <Grid item xs={6} md={2.4}>
                <SummaryCard
                  label="Errors"
                  value={get(validation, "errorCount", "ErrorCount", 0)}
                  icon={<ErrorOutlineIcon />}
                />
              </Grid>
              <Grid item xs={6} md={2.4}>
                <SummaryCard
                  label="MPC Members"
                  value={get(validation, "mpcMemberCount", "MpcMemberCount", 0)}
                  icon={<HowToRegOutlinedIcon />}
                />
              </Grid>
            </Grid>
          ) : null}

          {validationIssues.slice(0, 5).map((issue, index) => (
            <Alert
              key={`${get(issue, "issueCode", "IssueCode", "ISSUE")}-${index}`}
              severity={
                normalizeText(
                  get(issue, "severity", "Severity", "ERROR"),
                ).toUpperCase() === "WARNING"
                  ? "warning"
                  : "error"
              }
            >
              <strong>
                {get(issue, "issueCode", "IssueCode", "CONFIGURATION_ISSUE")}
              </strong>{" "}
              — {get(issue, "message", "Message", "")}
            </Alert>
          ))}
        </Stack>
      </Paper>

      {validationBlocked ? (
        <Alert severity="warning" icon={<ErrorOutlineIcon />}>
          The workflow configuration has validation errors. Simulation is still
          available for diagnosis; the backend resolver will show whether the
          selected EPO is affected by those errors.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.4 }}>
        <Stack spacing={1.25}>
          <Box>
            <Typography variant="h6">Simulation Input</Typography>
            <Typography variant="body2" color="text.secondary">
              Select an office employee and amount. The backend resolves the
              same DB rules regardless of how the approval sequence was edited.
            </Typography>
          </Box>

          <Grid container spacing={0.8} alignItems="center" wrap="nowrap">
            {/* EPO Creator */}
            <Grid item xs={4} md={3}>
              <Autocomplete
                size="small"
                options={employees}
                value={creator}
                onChange={(_, value) => {
                  setCreator(value);
                  setResult(null);
                }}
                filterOptions={filterEmployees}
                getOptionLabel={(option) =>
                  option?.displayName || option?.fullName || ""
                }
                isOptionEqualToValue={(option, value) =>
                  Number(option.userId) === Number(value.userId)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="EPO Creator"
                    placeholder="Search name / employee ID"
                  />
                )}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={2} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Amount"
                value={amount}
                onChange={(event) => {
                  setAmount(event.target.value);
                  setResult(null);
                }}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>

            {/* Currency */}
            <Grid item xs={2} md={2}>
              <Autocomplete
                size="small"
                options={currencies}
                value={selectedCurrency}
                onChange={(_, value) => changeCurrency(value)}
                getOptionLabel={(option) =>
                  `${option?.code || ""}${
                    option?.name ? ` - ${option.name}` : ""
                  }`
                }
                isOptionEqualToValue={(option, value) =>
                  Number(option.id) === Number(value.id)
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Currency" />
                )}
              />
            </Grid>

            {/* INR Rate */}
            <Grid item xs={2} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="INR Rate"
                value={exchangeRate}
                onChange={(event) => {
                  setExchangeRate(event.target.value);
                  setResult(null);
                }}
                disabled={
                  Number(selectedCurrency?.id || 1) === 1 ||
                  selectedCurrency?.code === "INR" ||
                  currencyLoading
                }
                InputProps={{
                  endAdornment: currencyLoading ? (
                    <CircularProgress size={16} />
                  ) : null,
                }}
              />
            </Grid>

            {/* IT Purchase */}
            <Grid
              item
              xs={2}
              md={3}
              sx={{
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              <FormControlLabel
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                  },
                }}
                control={
                  <Switch
                    size="small"
                    checked={isItPurchase}
                    onChange={(event) => {
                      setIsItPurchase(event.target.checked);
                      setResult(null);
                    }}
                  />
                }
                label="IT Purchase"
              />
            </Grid>
            <Grid
              item
              md={2}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                ml: "auto",
              }}
            >
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={
                  running ? <CircularProgress size={15} /> : <PlayArrowIcon />
                }
                disabled={running || !creator || amount === ""}
                onClick={runSimulation}
                sx={{
                  whiteSpace: "nowrap",
                  minWidth: 180,
                }}
              >
                Simulate Current DB Workflow
              </Button>
            </Grid>
          </Grid>

          {creator ? (
            <Alert severity="info">
              Creator:{" "}
              <strong>{creator.displayName || creator.fullName}</strong>
              {creator.departmentName ? ` • ${creator.departmentName}` : ""}
              {creator.empLimitNew != null
                ? ` • Current EmpLimit ${formatCurrency(creator.empLimitNew)}`
                : ""}
            </Alert>
          ) : null}

          {currencyError ? (
            <Alert severity="warning">{currencyError}</Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </Paper>

      {summary ? (
        <>
          <Grid container spacing={0.9}>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                label="Matched Rule"
                value={
                  get(
                    summary,
                    "matchedRuleCode",
                    "MatchedRuleCode",
                    "No match",
                  ) || "No match"
                }
                caption={get(summary, "matchedRuleName", "MatchedRuleName", "")}
                icon={<RuleOutlinedIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                label="Rule Priority"
                value={
                  get(
                    summary,
                    "matchedRulePriority",
                    "MatchedRulePriority",
                    "—",
                  ) ?? "—"
                }
                caption={
                  get(summary, "isItPurchase", "IsItPurchase", false)
                    ? "IT Purchase"
                    : "Normal EPO"
                }
                icon={<CheckCircleOutlineIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                label="Comparison Amount"
                value={formatCurrency(
                  get(summary, "baseAmountInr", "BaseAmountInr", null),
                )}
                caption="All rule limits are compared in INR"
                icon={<RuleOutlinedIcon />}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                label="Resolved Steps"
                value={steps.length}
                caption={`${approvers.length} resolved approver row${
                  approvers.length === 1 ? "" : "s"
                }`}
                icon={<HowToRegOutlinedIcon />}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 0.9 }}>
            <Stack spacing={0.8}>
              <Typography variant="subtitle1" fontWeight={700}>
                Effective Limits Used
              </Typography>
              <Grid container spacing={0.8}>
                <Grid item xs={6} md={2.4}>
                  <SummaryCard
                    label="Creator"
                    value={formatCurrency(
                      get(summary, "creatorLimit", "CreatorLimit", null),
                    )}
                    icon={<RuleOutlinedIcon />}
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <SummaryCard
                    label="Department Deputy"
                    value={formatCurrency(
                      get(
                        summary,
                        "departmentDeputyLimit",
                        "DepartmentDeputyLimit",
                        null,
                      ),
                    )}
                    icon={<RuleOutlinedIcon />}
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <SummaryCard
                    label="MPC"
                    value={formatCurrency(
                      get(summary, "mpcLimit", "MpcLimit", null),
                    )}
                    icon={<RuleOutlinedIcon />}
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <SummaryCard
                    label="Higher Authority"
                    value={formatCurrency(
                      get(
                        summary,
                        "higherAuthorityLimit",
                        "HigherAuthorityLimit",
                        null,
                      ),
                    )}
                    icon={<RuleOutlinedIcon />}
                  />
                </Grid>
                <Grid item xs={6} md={2.4}>
                  <SummaryCard
                    label="IT Approver"
                    value={formatCurrency(
                      get(summary, "itApproverLimit", "ItApproverLimit", null),
                    )}
                    icon={<RuleOutlinedIcon />}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.25 }}>
            <Stack spacing={0.9}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Rule Evaluation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rules are checked by Priority. The first complete match is
                  selected.
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell>Rule</TableCell>
                      <TableCell>Minimum</TableCell>
                      <TableCell>Maximum</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {evaluations.map((row) => {
                      const matched = Boolean(
                        get(row, "isMatched", "IsMatched", false),
                      );
                      return (
                        <TableRow
                          key={get(
                            row,
                            "ruleId",
                            "RuleId",
                            get(row, "ruleCode", "RuleCode", ""),
                          )}
                          selected={matched}
                        >
                          <TableCell>
                            {get(row, "priority", "Priority", "—")}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {get(row, "ruleCode", "RuleCode", "—")}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {get(row, "ruleName", "RuleName", "")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {boundaryText(
                              get(
                                row,
                                "minLimitSource",
                                "MinLimitSource",
                                "NONE",
                              ),
                              get(row, "minLimitKey", "MinLimitKey", null),
                              get(
                                row,
                                "minResolvedAmount",
                                "MinResolvedAmount",
                                null,
                              ),
                              Boolean(
                                get(row, "minInclusive", "MinInclusive", false),
                              ),
                              true,
                            )}
                          </TableCell>
                          <TableCell>
                            {boundaryText(
                              get(
                                row,
                                "maxLimitSource",
                                "MaxLimitSource",
                                "NONE",
                              ),
                              get(row, "maxLimitKey", "MaxLimitKey", null),
                              get(
                                row,
                                "maxResolvedAmount",
                                "MaxResolvedAmount",
                                null,
                              ),
                              Boolean(
                                get(row, "maxInclusive", "MaxInclusive", true),
                              ),
                              false,
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={matched ? "success" : "default"}
                              variant={matched ? "filled" : "outlined"}
                              label={matched ? "MATCHED" : "NO"}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {get(
                                row,
                                "evaluationMessage",
                                "EvaluationMessage",
                                "",
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.25 }}>
            <Stack spacing={1}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Resolved Approval Sequence
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This is produced from master.EpoWorkflowStep plus the current
                  authority/user configuration. No sequence is hardcoded in
                  React.
                </Typography>
              </Box>

              {steps.length ? (
                <Stepper orientation="vertical" activeStep={-1}>
                  {steps.map((step) => {
                    const stepApprovers = approversForStep(step);
                    const isResolved = Boolean(
                      get(step, "isResolved", "IsResolved", false),
                    );
                    return (
                      <Step
                        key={get(
                          step,
                          "workflowStepId",
                          "WorkflowStepId",
                          get(step, "stepOrder", "StepOrder", 0),
                        )}
                        active
                        completed={false}
                      >
                        <StepLabel error={!isResolved}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Typography fontWeight={700}>
                              Step {get(step, "stepOrder", "StepOrder", "—")} —{" "}
                              {get(
                                step,
                                "stepName",
                                "StepName",
                                get(step, "stepCode", "StepCode", ""),
                              )}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={get(
                                step,
                                "approvalPolicy",
                                "ApprovalPolicy",
                                "SINGLE",
                              )}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${get(
                                step,
                                "effectiveRequiredApprovalCount",
                                "EffectiveRequiredApprovalCount",
                                0,
                              )} required`}
                            />
                            <Chip
                              size="small"
                              color={isResolved ? "success" : "error"}
                              variant="outlined"
                              label={
                                isResolved ? "Resolved" : "Missing approver"
                              }
                            />
                          </Stack>
                        </StepLabel>
                        <Box sx={{ ml: { xs: 2, sm: 4.5 }, mt: 1, mb: 2 }}>
                          <Stack spacing={1}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Source:{" "}
                              {get(
                                step,
                                "approverSourceType",
                                "ApproverSourceType",
                                "—",
                              )}
                              {get(step, "approverKey", "ApproverKey", "")
                                ? ` / ${get(
                                    step,
                                    "approverKey",
                                    "ApproverKey",
                                    "",
                                  )}`
                                : ""}
                            </Typography>
                            {stepApprovers.length ? (
                              <Grid container spacing={1}>
                                {stepApprovers.map((person, index) => (
                                  <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={4}
                                    key={`${get(
                                      person,
                                      "workflowStepId",
                                      "WorkflowStepId",
                                      0,
                                    )}-${get(
                                      person,
                                      "userId",
                                      "UserId",
                                      index,
                                    )}-${index}`}
                                  >
                                    <Paper variant="outlined" sx={{ p: 1.25 }}>
                                      <PersonRow person={person} />
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Alert severity="error">
                                No approver resolved for this step.
                              </Alert>
                            )}
                          </Stack>
                        </Box>
                      </Step>
                    );
                  })}
                </Stepper>
              ) : (
                <Alert severity="error">
                  No approval sequence was resolved.
                </Alert>
              )}
            </Stack>
          </Paper>

          {mails.length ? (
            <Paper variant="outlined" sx={{ p: 1.25 }}>
              <Stack spacing={1.2}>
                <Box>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Mail Simulation
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        These are the real recipients, real templates and real
                        rendered email bodies that the current EPO configuration
                        would use. Nothing is sent.
                      </Typography>
                    </Box>

                    <Chip
                      color="success"
                      variant="outlined"
                      label={`${mails.length} mail preview${
                        mails.length === 1 ? "" : "s"
                      }`}
                    />
                  </Stack>
                </Box>

                <Stack spacing={1.25}>
                  {mails.map((mail, index) => (
                    <MailPreview
                      key={`${get(
                        mail,
                        "eventCode",
                        "EventCode",
                        "MAIL",
                      )}-${index}`}
                      mail={mail}
                    />
                  ))}
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {issues.length ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Typography variant="h6">Simulation Issues</Typography>
                {issues.map((issue, index) => {
                  const severity = normalizeText(
                    get(issue, "severity", "Severity", "ERROR"),
                  ).toUpperCase();
                  return (
                    <Alert
                      key={`${get(
                        issue,
                        "issueCode",
                        "IssueCode",
                        "ISSUE",
                      )}-${index}`}
                      severity={severity === "WARNING" ? "warning" : "error"}
                    >
                      <strong>
                        {get(issue, "issueCode", "IssueCode", "ISSUE")}
                      </strong>{" "}
                      — {get(issue, "message", "Message", "")}
                    </Alert>
                  );
                })}
              </Stack>
            </Paper>
          ) : (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
              Simulation resolved successfully from the current database
              configuration.
            </Alert>
          )}
        </>
      ) : null}
    </Stack>
  );
};

export default EpoWorkflowSimulator;
